import { Edit } from "lucide-react";
import { ReactNode } from "react";
import { formatMoneyValue } from "@/app/utils/formatMoneyValue";
import { PartnerType } from "@/app/utils/partners.enum";
import { useProductsCatalog } from "@/app/hooks/useProductsCatalog";
import { useProductVisibility } from "@/app/hooks/useProductVisibility";
import { useAllPartnerTables } from "@/app/hooks/useAllPartnerTables";
import {
  isSingleCommissionPartner,
  stripCltPrefix,
} from "@/app/utils/partners";
import {
  getReadableTextColor,
  isLightBackground,
} from "@/app/utils/readableTextColor";

interface SupervisorCommissionsProps {
  commissions?: Record<string, any>;
  onEdit: () => void;
}

type CommissionRowProps = {
  label: string;
  value: string;
  badgeColor: string;
  subLabel?: ReactNode;
};

const CommissionRow = ({
  label,
  value,
  badgeColor,
  subLabel,
}: CommissionRowProps) => (
  <div className="flex items-center justify-between text-sm">
    <span className="font-medium">
      {label}
      {subLabel && (
        <span className="block text-xs font-normal opacity-80">{subLabel}</span>
      )}
    </span>
    <span
      className="rounded-full px-2.5 py-0.5 text-xs font-semibold"
      style={{ backgroundColor: badgeColor }}
    >
      {value}
    </span>
  </div>
);

export const SupervisorCommissions = ({
  commissions,
  onEdit,
}: SupervisorCommissionsProps) => {
  const { getProductLabel, getProductColor } = useProductsCatalog();
  const { isProductVisible } = useProductVisibility();
  const { getTable, isLoading: isLoadingTables } = useAllPartnerTables();

  const getTableName = (tableId?: string) =>
    getTable(tableId)?.name || "Nome indisponível";

  const getTableType = (tableId?: string) => getTable(tableId)?.type;

  const formatCommissionValue = (value: number, isMoney: boolean) => {
    if (isMoney) {
      return formatMoneyValue(value);
    } else {
      return `${Number(value).toFixed(2).replace(".", ",")}%`;
    }
  };

  const formatRangeValue = (value: number, isMoney: boolean) => {
    if (isMoney) {
      return formatMoneyValue(value);
    }
    return `${value} parcelas`;
  };

  const hasCommission = (item: any) =>
    item?.supervisorCommission !== undefined &&
    item?.supervisorCommission !== null;

  const buildCltGroups = (items: any[]) => {
    const groups = new Map<string, { id: string; name: string; rows: any[] }>();

    items.forEach((item) => {
      if (!hasCommission(item) || item.value === undefined) return;

      const tableId = String(item._id ?? "");
      if (!groups.has(tableId)) {
        groups.set(tableId, {
          id: tableId,
          name: getTable(tableId)?.name || "Tabela removida",
          rows: [],
        });
      }
      groups.get(tableId)?.rows.push(item);
    });

    return [...groups.values()]
      .map((group) => ({
        ...group,
        rows: [...group.rows].sort((a, b) => a.value - b.value),
      }))
      .sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id));
  };

  return (
    <div className="my-5 space-y-3">
      <div className="group flex items-center gap-2">
        <h2 className="font-semibold">Comissão supervisor</h2>
        <Edit
          onClick={onEdit}
          size={16}
          className="hidden cursor-pointer text-gray-500 hover:text-gray-700 group-hover:block"
        />
      </div>
      <div className="space-y-2">
        {!isLoadingTables &&
          Object.entries(commissions || {}).map(([partnerKey, config]) => {
            if (!config || !config.items || config.items.length === 0)
              return null;

            if (!isProductVisible(partnerKey)) return null;

            const isCommissionMoney = partnerKey === PartnerType.SALVCAR;
            const isRangeMoney =
              isCommissionMoney || partnerKey === PartnerType.PAN;
            const isCLT = partnerKey === PartnerType.CLT;
            const isSingleTable = isSingleCommissionPartner(partnerKey);
            const bgColor = getProductColor(partnerKey);
            const textColor = getReadableTextColor(bgColor);
            const badgeColor = isLightBackground(bgColor)
              ? "rgba(0,0,0,0.12)"
              : "rgba(255,255,255,0.25)";
            const productName = stripCltPrefix(
              getProductLabel(partnerKey as PartnerType),
            );

            const cltGroups = isCLT ? buildCltGroups(config.items) : [];
            if (isCLT && cltGroups.length === 0) return null;

            return (
              <div
                key={partnerKey}
                className="rounded-lg border p-3"
                style={{
                  backgroundColor: bgColor,
                  color: textColor,
                  borderColor: bgColor,
                }}
              >
                <p className="mb-2 text-xs font-bold uppercase tracking-wider opacity-90">
                  {getProductLabel(partnerKey as PartnerType)}
                </p>

                {isCLT ? (
                  <div className="space-y-3">
                    {cltGroups.map((group) => (
                      <div key={group.id}>
                        <p className="text-xs font-semibold uppercase tracking-wide opacity-75">
                          {group.name}
                        </p>
                        <div className="mt-1 space-y-1.5">
                          {group.rows.map((item: any, idx: number) => (
                            <CommissionRow
                              key={idx}
                              label={`A partir de ${formatRangeValue(item.value, false)}`}
                              value={formatCommissionValue(
                                item.supervisorCommission,
                                false,
                              )}
                              badgeColor={badgeColor}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {config.items.map((item: any, idx: number) => {
                      if (!hasCommission(item)) return null;

                      let label = "";
                      if (isSingleTable) {
                        label = productName;
                      } else if (item.value !== undefined) {
                        label = `A partir de ${formatRangeValue(item.value, isRangeMoney)}`;
                      } else if (item._id) {
                        // FGTS (e CLT antigo) guardam uma comissão por tabela.
                        const tableName = getTableName(item._id);
                        label =
                          getTableType(item._id)?.toUpperCase() === "CLT"
                            ? `${tableName} parcelas`
                            : tableName;
                      }

                      return (
                        <CommissionRow
                          key={idx}
                          label={label || productName}
                          value={formatCommissionValue(
                            item.supervisorCommission,
                            isCommissionMoney,
                          )}
                          badgeColor={badgeColor}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
};
